import _ from "lodash";

import Box from "components/Box";

import PrintableMemoDDSection from "pages/Deal/PrintableMemo/components/PrintableDDSection";
import PrintableOverviewCommunityAmenities from "pages/Deal/PrintableMemo/components/PrintableOverviewCommunityAmenities";
import PrintableOverviewSchedule from "pages/Deal/PrintableMemo/components/PrintableOverviewSectionSchedule";
import PrintableOverviewUnitAmenities from "pages/Deal/PrintableMemo/components/PrintableOverviewUnitAmenities";
import PrintableRisksAndMitigants from "pages/Deal/PrintableMemo/components/PrintableRiskAndMitigants";
import PrintableSectionAIAnswer from "pages/Deal/PrintableMemo/components/PrintableSectionAIAnswer";
import PrintableSectionAISummaryResponse from "pages/Deal/PrintableMemo/components/PrintableSectionAISummaryResponse";
import PrintableChecklistTable from "pages/Deal/PrintableMemo/components/PrintableSectionChecklistTable";
import PrintableSectionComment from "pages/Deal/PrintableMemo/components/PrintableSectionComment";
import PrintableSectionDevelopmentBudget from "pages/Deal/PrintableMemo/components/PrintableSectionDevelopementBudget";
import PrintableSectionExcelTable from "pages/Deal/PrintableMemo/components/PrintableSectionExcelTable";
import PrintableSectionFloorArea from "pages/Deal/PrintableMemo/components/PrintableSectionFloorArea";
import PrintableMemoSectionHeader from "pages/Deal/PrintableMemo/components/PrintableSectionHeader";
import PrintableSectionImage from "pages/Deal/PrintableMemo/components/PrintableSectionImage";
import PrintableSectionIncomeAndOpex from "pages/Deal/PrintableMemo/components/PrintableSectionIncomeAndOpex";
import PrintableSectionLocation from "pages/Deal/PrintableMemo/components/PrintableSectionLocation";
import PrintableSectionProjectDetails from "pages/Deal/PrintableMemo/components/PrintableSectionProjectDetails";
import PrintableSectionProjectNarrative from "pages/Deal/PrintableMemo/components/PrintableSectionProjectNarrative";
import PrintableSectionSourcesAndUses from "pages/Deal/PrintableMemo/components/PrintableSectionSourcesAndUses";
import PrintableSectionSummary from "pages/Deal/PrintableMemo/components/PrintableSectionSummary";
import PrintableSectionTeam from "pages/Deal/PrintableMemo/components/PrintableSectionTeam";
import PrintableSectionUnitMixAndRent from "pages/Deal/PrintableMemo/components/PrintableSectionUnitMixAndRent";

import { IDeal } from "entities/Deal/sdk";
import MemoHeader from "entities/Memo/components/Sections/MemoHeader";
import ShareableMemoHeader from "entities/Memo/components/Sections/ShareableMemoHeader";
import { IMemoSection } from "entities/Memo/sdk";
import { EMemoSectionType } from "entities/MemoTemplate/constants";

interface IMemoSectionWrapper {
  section: IMemoSection;
  deal: IDeal;
  externalToken?: string;
}

const PrintableSection = ({
  section,
  deal,
  externalToken,
}: IMemoSectionWrapper) => {
  switch (section.section_type) {
    case EMemoSectionType.MEMO_HEADER:
      return _.isNil(externalToken) ? <MemoHeader /> : <ShareableMemoHeader />;
    case EMemoSectionType.SECTION_HEADER:
      return <PrintableMemoSectionHeader section={section} />;
    case EMemoSectionType.DUE_DILIGENCE:
      return <PrintableMemoDDSection section={section} />;
    case EMemoSectionType.PROJECT_DETAILS:
      return (
        <PrintableSectionProjectDetails
          deal={deal}
          section={section}
          externalToken={externalToken}
        />
      );
    case EMemoSectionType.LOCATION:
      return <PrintableSectionLocation deal={deal} section={section} />;
    case EMemoSectionType.EXECUTIVE_SUMMARY:
      return (
        <PrintableSectionSummary
          category="Location Descriptions"
          dealId={deal.id}
          section={section}
          externalToken={externalToken}
        />
      );
    case EMemoSectionType.RISKS_AND_MITIGATIONS:
      return (
        <PrintableSectionSummary
          category="Risks and Mitigating Factors"
          dealId={deal.id}
          section={section}
          externalToken={externalToken}
        />
      );
    case EMemoSectionType.FINAL_RECOMMENDATIONS:
      return (
        <PrintableSectionSummary
          category="Final Recommendation"
          dealId={deal.id}
          section={section}
          externalToken={externalToken}
        />
      );
    case EMemoSectionType.PROJECT_NARRATIVE:
      return (
        <PrintableSectionProjectNarrative
          deal={deal}
          section={section}
          externalToken={externalToken}
        />
      );
    case EMemoSectionType.SOURCE_AND_USES:
      return (
        <PrintableSectionSourcesAndUses
          dealId={deal.id}
          externalToken={externalToken}
          section={section}
        />
      );
    case EMemoSectionType.UNIT_MIX_AND_RENT:
      return (
        <PrintableSectionUnitMixAndRent
          dealId={deal.id}
          externalToken={externalToken}
          section={section}
        />
      );
    case EMemoSectionType.FLOOR_AREA:
      return (
        <PrintableSectionFloorArea
          dealId={deal.id}
          externalToken={externalToken}
          section={section}
        />
      );
    case EMemoSectionType.INCOME_AND_OPEX:
      return (
        <PrintableSectionIncomeAndOpex
          dealId={deal.id}
          externalToken={externalToken}
        />
      );
    case EMemoSectionType.SCHEDULE:
      return (
        <PrintableOverviewSchedule
          dealId={deal.id}
          externalToken={externalToken}
        />
      );
    case EMemoSectionType.UNIT_AMENITIES:
      return (
        <PrintableOverviewUnitAmenities
          dealId={deal.id}
          externalToken={externalToken}
        />
      );
    case EMemoSectionType.COMMUNITY_AMENITIES:
      return (
        <PrintableOverviewCommunityAmenities
          dealId={deal.id}
          externalToken={externalToken}
        />
      );
    case EMemoSectionType.DEVELOPMENT_BUDGET:
      return (
        <PrintableSectionDevelopmentBudget
          dealId={deal.id}
          externalToken={externalToken}
          section={section}
        />
      );
    case EMemoSectionType.DEVELOPER:
      return (
        <PrintableSectionTeam
          dealId={deal.id}
          groupName="Developer"
          externalToken={externalToken}
          section={section}
        />
      );
    case EMemoSectionType.ARCHITECT:
      return (
        <PrintableSectionTeam
          dealId={deal.id}
          groupName="Architect"
          externalToken={externalToken}
          section={section}
        />
      );
    case EMemoSectionType.GENERAL_CONTRACTOR:
      return (
        <PrintableSectionTeam
          dealId={deal.id}
          groupName="General Contractor"
          externalToken={externalToken}
          section={section}
        />
      );
    case EMemoSectionType.PROPERTY_MANAGER:
      return (
        <PrintableSectionTeam
          dealId={deal.id}
          groupName="Property Manager"
          externalToken={externalToken}
          section={section}
        />
      );
    case EMemoSectionType.DEVELOPER_PARTNER:
      return (
        <PrintableSectionTeam
          dealId={deal.id}
          groupName="Developer Partner"
          externalToken={externalToken}
          section={section}
        />
      );
    case EMemoSectionType.IMAGE:
      return <PrintableSectionImage section={section} />;
    case EMemoSectionType.COMMENT:
      return <PrintableSectionComment section={section} />;
    case EMemoSectionType.EXCEL_TABLE:
      return <PrintableSectionExcelTable section={section} />;
    case EMemoSectionType.CHECKLIST_TABLE:
      return <PrintableChecklistTable section={section} />;
    case EMemoSectionType.AI_SUMMARY_RESPONSE:
      return (
        <PrintableSectionAISummaryResponse
          section={section}
          dealId={deal.id}
          externalToken={externalToken}
        />
      );
    case EMemoSectionType.AI_ANSWER:
      return (
        <PrintableSectionAIAnswer
          section={section}
          externalToken={externalToken}
        />
      );
    case EMemoSectionType.RISKS_AND_MITIGANTS:
      return <PrintableRisksAndMitigants section={section} />;
  }
  return null;
};

const PrintableMemoSectionWrapper = ({
  section,
  deal,
  externalToken,
}: IMemoSectionWrapper) => (
  <Box
    sx={{
      breakBefore: section.break_before ? "page" : "auto",
      "& .MuiIconButton-root": { visibility: "hidden" },
      "&:hover .MuiIconButton-root": {
        visibility: "visible",
      },
    }}
    data-testid="memo-section"
    id={section.section_id.toString()}
  >
    <PrintableSection
      section={section}
      deal={deal}
      externalToken={externalToken}
    />
  </Box>
);

export default PrintableMemoSectionWrapper;
